import React, { useEffect, useMemo, useState } from 'react';
import Dialog from './TivigoDialog';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import RateReviewRoundedIcon from '@mui/icons-material/RateReviewRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import StarRoundedIcon from '@mui/icons-material/StarRounded';
import './userPanelReviewModal.css';

const RATING_LABELS = ['', 'Beğenmedim', 'Vasat', 'İyi', 'Çok iyi', 'Harika'];

function UserPanelReviewModal({ open, onClose, content, contentType, onSubmit }) {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);

  const isSetup = contentType === 'setup';
  const contentName = String(content?.name || content?.title || (isSetup ? 'Uygulama' : 'İçerik'));
  const poster = isSetup
    ? content?.logo || content?.poster || content?.cover || content?.stream_icon || ''
    : content?.poster || content?.cover || content?.stream_icon || '';
  const contentLabel = isSetup ? 'Uygulama' : contentType === 'series' ? 'Dizi' : 'Film';
  const activeRating = hoverRating || rating;
  const pageType = isSetup ? 'kurulum-detay' : contentType === 'series' ? 'dizi' : 'film';
  const pageRef = useMemo(() => {
    if (contentType === 'setup') return content?.id || '';
    if (contentType === 'series') return content?.series_id || content?.id || '';
    return content?.stream_id || content?.id || '';
  }, [content, contentType]);

  useEffect(() => {
    if (!open) return;
    setRating(0);
    setHoverRating(0);
    setComment('');
    setSubmitting(false);
    setStatus(null);
  }, [content, open]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const trimmedComment = comment.trim();

    if (rating === 0 && trimmedComment.length < 10) {
      setStatus({ type: 'error', text: 'Bir yıldız puanı verin veya en az 10 karakter yorum yazın.' });
      return;
    }

    if (trimmedComment.length > 0 && trimmedComment.length < 10) {
      setStatus({ type: 'error', text: 'Yorum en az 10 karakter olmalıdır.' });
      return;
    }

    setSubmitting(true);
    setStatus(null);

    try {
      if (typeof onSubmit !== 'function') throw new Error('Yorum gönderme bağlantısı kullanılamıyor.');
      await onSubmit({
        content,
        contentType,
        sayfaTipi: pageType,
        sayfaRef: String(pageRef),
        rating,
        yorum: trimmedComment || 'Değerlendirme yapıldı.',
      });

      setStatus({
        type: 'success',
        text: 'Değerlendirmeniz alındı. Onaylandıktan sonra yayınlanacaktır.',
      });
      setRating(0);
      setHoverRating(0);
      setComment('');
    } catch (error) {
      setStatus({
        type: 'error',
        text: error?.message || 'Değerlendirmeniz gönderilemedi. Lütfen tekrar deneyin.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth={false}
      keepMounted
      transitionDuration={0}
      disableEscapeKeyDown
      className="tvup-review-dialog"
      aria-labelledby="tvup-review-modal-title"
      slotProps={{
        backdrop: { className: 'tvup-review-backdrop' },
        paper: { id: 'tvup-review-modal', className: 'tvup-review-modal' },
      }}
    >
      <header className="tvup-review-header">
        <span className="tvup-review-title-icon" aria-hidden="true"><RateReviewRoundedIcon /></span>
        <div className="tvup-review-title-copy">
          <h2 id="tvup-review-modal-title">Sen de Yorum Yap</h2>
          <p><span>{contentName}</span> · {contentLabel}</p>
        </div>
        <button type="button" className="tvup-review-close" aria-label="Yorum yapma penceresini kapat" onClick={onClose}>
          <CloseRoundedIcon />
        </button>
      </header>

      <form className="tvup-review-form" onSubmit={handleSubmit}>
        <div className="tvup-review-form-content">
          <section className="tvup-review-content-summary">
            <span className={`tvup-review-poster${isSetup ? ' is-setup' : ''}`} aria-hidden="true">
              {poster ? <img src={poster} alt="" loading="lazy" referrerPolicy="no-referrer" /> : <b>{contentName.charAt(0)}</b>}
            </span>
            <div>
              <small>{contentLabel.toLocaleUpperCase('tr-TR')} DEĞERLENDİRMESİ</small>
              <h3>{contentName}</h3>
              <p>
                {isSetup
                  ? 'Kurulum ve kullanım deneyimini Tivigo aboneleriyle paylaş.'
                  : 'Puanını ver, izlenimini Tivigo aboneleriyle paylaş.'}
              </p>
            </div>
          </section>

          <section className="tvup-review-rating-block">
            <div className="tvup-review-field-heading">
              <div>
                <h3>{isSetup ? 'Uygulama deneyimin nasıldı?' : 'Kaç yıldız verirsin?'}</h3>
                <p>{isSetup ? 'Kurulum ve kullanımı 5 üzerinden değerlendir.' : 'Deneyimini 5 üzerinden değerlendir.'}</p>
              </div>
              <span aria-live="polite">{activeRating ? RATING_LABELS[activeRating] : 'Puan seç'}</span>
            </div>
            <div className="tvup-review-stars" role="radiogroup" aria-label={`${contentName} yıldız puanı`} onMouseLeave={() => setHoverRating(0)}>
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={rating === value}
                  aria-label={`${value} yıldız ver`}
                  className={value <= activeRating ? 'is-active' : ''}
                  onMouseEnter={() => setHoverRating(value)}
                  onFocus={() => setHoverRating(value)}
                  onBlur={() => setHoverRating(0)}
                  onClick={() => {
                    setRating(value);
                    setStatus(null);
                  }}
                >
                  <StarRoundedIcon />
                </button>
              ))}
            </div>
          </section>

          <label className="tvup-review-text-field">
            <span>Yorumun <small>İsteğe bağlı</small></span>
            <textarea
              value={comment}
              rows={4}
              maxLength={1000}
              aria-label={`${contentName} için ${isSetup ? 'uygulama deneyiminiz' : 'yorumunuz'}`}
              onChange={(event) => {
                setComment(event.target.value);
                setStatus(null);
              }}
            />
            <small>{comment.length} / 1000 karakter</small>
          </label>

          {status && (
            <div className={`tvup-review-status is-${status.type}`} role={status.type === 'error' ? 'alert' : 'status'}>
              {status.type === 'success' && <CheckCircleRoundedIcon aria-hidden="true" />}
              <span>{status.text}</span>
            </div>
          )}
        </div>

        <footer className="tvup-review-footer">
          <span>{isSetup ? 'Uygulama yorumları yayınlanmadan önce kontrol edilir.' : 'Yorumlar yayınlanmadan önce kontrol edilir.'}</span>
          <button
            type="submit"
            disabled={submitting || (rating === 0 && comment.trim().length === 0)}
          >
            <SendRoundedIcon aria-hidden="true" />
            {submitting ? 'Gönderiliyor...' : 'Değerlendirmeyi Gönder'}
          </button>
        </footer>
      </form>
    </Dialog>
  );
}

export default UserPanelReviewModal;
